/* ================================
  PATCHED product-info.js (PG Swiper) — DIV VARIANT PICKER + SMART MOBILE SCROLL (PRODUCTION)
  - Fixes variant image switching for Swiper markup (div.swiper-slide)
  - Keeps Dawn behavior for hide_variants by syncing DOM to freshly fetched HTML
  - Works with <media-gallery> + #GalleryViewer- / #GalleryThumbnails- IDs
  - UPDATED:
    ✅ getOptionGroupNameFromTarget() works with DIV-based variant picker (.variant-head/.form__label)
    ✅ smartScrollToGallery() uses sticky-safe offset + double-rAF for stable scroll after media update
    ✅ production: cancels in-flight scroll animations + won’t fight user scroll
================================ */

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');

        // scroll anim internals
        this._scrollAnimRaf = null;
        this._scrollAnimActive = false;
        this._userIsScrolling = false;
        this._userScrollT = null;
        this._onUserScroll = null;

        this._lastOptionGroupName = '';
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        // Premium: don't fight user scroll
        this._onUserScroll = () => {
          this._userIsScrolling = true;
          clearTimeout(this._userScrollT);
          this._userScrollT = setTimeout(() => (this._userIsScrolling = false), 120);
        };
        window.addEventListener('scroll', this._onUserScroll, { passive: true });

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();

        window.removeEventListener('scroll', this._onUserScroll);
        clearTimeout(this._userScrollT);
        this._cancelScrollAnim();
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push(() => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // ✅ remember which option group user interacted with (Color/Style/Size)
        this._lastOptionGroupName = this.getOptionGroupNameFromTarget(event.target);

        // if user is changing variants quickly, cancel in-flight scroll
        this._cancelScrollAnim();

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((r) => r.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode?.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];
        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
        if (optionValues.length) params.push(`option_values=${optionValues.join(',')}`);
        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          // ✅ SWIPER: activate featured media
          this.updateMedia(html, variant?.featured_media?.id);

          // ✅ Mobile: SMART scroll for Color/Style changes (not Size)
          if (window.matchMedia('(max-width: 749px)').matches && this.shouldAutoScrollGallery(this._lastOptionGroupName)) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => this.smartScrollToGallery());
            });
          }

          const updateSourceFromDestination = (id, shouldHide = () => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, html, variant },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`);
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      /* ===========================
        SWIPER MEDIA SYNC
      =========================== */
      updateMedia(html, variantFeaturedMediaId) {
        const gallery = this.querySelector('media-gallery');
        if (!gallery) return;

        if (!variantFeaturedMediaId) return;

        // IMPORTANT: ALWAYS build the id with this.dataset.section
        const targetMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;

        if (typeof gallery.onVariantChange === 'function') {
          gallery.onVariantChange(targetMediaId);
          return;
        }

        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(targetMediaId, true);
          return;
        }

        gallery.dispatchEvent(new CustomEvent('pg:reinit', { bubbles: true }));
      }

      /* ===========================
        MOBILE SMART SCROLL (sticky-safe)
      =========================== */
      getScrollDurationMs() {
        return 750; // 650–900 feels premium
      }

      easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      }

      _cancelScrollAnim() {
        if (this._scrollAnimRaf) {
          cancelAnimationFrame(this._scrollAnimRaf);
          this._scrollAnimRaf = null;
        }
        this._scrollAnimActive = false;
      }

      animateScrollToY(targetY, durationMs = 750) {
        const prefersReduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        if (prefersReduce) {
          window.scrollTo(0, targetY);
          return;
        }

        // cancel any previous animation
        this._cancelScrollAnim();

        const startY = window.scrollY || window.pageYOffset || 0;
        const delta = targetY - startY;
        if (Math.abs(delta) < 2) return;

        const startT = performance.now();
        const dur = Math.max(140, Number(durationMs) || 750);

        this._scrollAnimActive = true;

        const tick = (now) => {
          if (!this._scrollAnimActive) return;

          const t = Math.min(1, (now - startT) / dur);
          const eased = this.easeInOutCubic(t);

          window.scrollTo(0, startY + delta * eased);

          if (t < 1) {
            this._scrollAnimRaf = requestAnimationFrame(tick);
          } else {
            this._scrollAnimRaf = null;
            this._scrollAnimActive = false;
          }
        };

        this._scrollAnimRaf = requestAnimationFrame(tick);
      }

      smartScrollToGallery() {
        const gallery = this.querySelector('media-gallery');
        if (!gallery) return;

        // don't fight user scroll
        if (this._userIsScrolling) return;

        const cssOffsetRaw = getComputedStyle(document.documentElement).getPropertyValue('--pg-sticky-offset').trim();
        let stickyOffset = 72;
        if (cssOffsetRaw) {
          const n = parseFloat(cssOffsetRaw);
          if (Number.isFinite(n)) stickyOffset = n;
        }

        const rect = gallery.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;

        // If already in a good place, skip
        const topOk = rect.top >= stickyOffset && rect.top <= stickyOffset + 120;
        const visiblePx = Math.min(rect.bottom, viewportH) - Math.max(rect.top, 0);
        const visibleRatio = rect.height > 0 ? visiblePx / rect.height : 0;
        if (topOk && visibleRatio >= 0.35) return;

        const y = (window.scrollY || 0) + rect.top - stickyOffset;
        const maxY = Math.max(0, document.documentElement.scrollHeight - viewportH);
        const targetY = Math.min(Math.max(0, y), maxY);

        this.animateScrollToY(targetY, this.getScrollDurationMs());
      }

      /* ===========================
        OPTION GROUP DETECTION (DIV-based)
      =========================== */
      getOptionGroupNameFromTarget(targetEl) {
        if (!targetEl) return '';

        const wrapper = targetEl.closest('.product-form__input');
        if (!wrapper) return '';

        const headLabel = wrapper.querySelector('.variant-head .form__label');
        if (headLabel?.textContent) return headLabel.textContent.trim().toLowerCase();

        const label = wrapper.querySelector('.form__label, label');
        if (label?.textContent) return label.textContent.trim().toLowerCase();

        return '';
      }

      shouldAutoScrollGallery(optionGroupName) {
        const n = (optionGroupName || '').toLowerCase();
        if (!n) return false;

        if (n.includes('size')) return false;
        if (n.includes('color') || n.includes('colour')) return true;
        if (n.includes('style') || n.includes('pattern') || n.includes('finish')) return true;

        return false;
      }

      /* ===========================
        Quantity (unchanged)
      =========================== */
      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) this.quantityInput.max = max;
        else this.quantityInput.removeAttribute('max');

        this.quantityInput.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((r) => r.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated?.querySelector(selector);
          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
              else current.removeAttribute(attribute);
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}